import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/Navbar';

import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import OffersList from './pages/OffersList';
import OfferDetails from './pages/OfferDetails';
import NewOffer from './pages/NewOffer';
import OfferChoice from './pages/OfferChoice';
import ClientDashboard from './pages/ClientDashboard';
import CompanyDashboard from './pages/CompanyDashboard';
import CompaniesList from './pages/CompaniesList';
import CompanyDetails from './pages/CompanyDetails';
import CompanyProfile from './pages/CompanyProfile';
import Messages from './pages/Messages';
import Chat from './pages/Chat';
import Subscription from './pages/Subscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import PromotionSuccess from './pages/PromotionSuccess';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import Regulamin from './pages/Regulamin';
import PolitykaPrywatnosci from './pages/PolitykaPrywatnosci';
import RegulaminPlatnosci from './pages/RegulaminPlatnosci';

// New features
import MaterialsList from './pages/MaterialsList';
import MaterialDetails from './pages/MaterialDetails';
import NewMaterial from './pages/NewMaterial';
import JobsList from './pages/JobsList';
import JobDetails from './pages/JobDetails';
import NewJob from './pages/NewJob';

// Lokalne strony SEO
import CityLanding from './pages/CityLanding';
import CityHub from './pages/CityHub';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hook do śledzenia odwiedzin strony
const usePageTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Śledź odwiedziny (fire and forget)
    fetch(`${BACKEND_URL}/api/track-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: location.pathname })
    }).catch(() => {}); // Ignore errors
  }, [location.pathname]);
};

const ProtectedRoute = ({ children, requireCompany = false, requireAdmin = false }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireCompany && user?.user_type !== 'company') {
    return <Navigate to="/dashboard/individual" replace />;
  }

  if (requireAdmin && !user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRouter = () => {
  const location = useLocation();
  
  // Track page visits
  usePageTracking();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const hideNavbar = ['/login', '/register', '/auth/callback', '/forgot-password', '/reset-password'].includes(location.pathname);

  return (
    <>
      {!hideNavbar && <Navbar />}
      
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        <Route path="/offers" element={<OffersList />} />
        <Route path="/offers/:offerId" element={<OfferDetails />} />
        
        <Route path="/companies" element={<CompaniesList />} />
        <Route path="/companies/:companyId" element={<CompanyDetails />} />
        
        <Route path="/new-offer" element={<ProtectedRoute><NewOffer /></ProtectedRoute>} />
        <Route path="/offer-choice" element={<OfferChoice />} />
        <Route path="/offers/new" element={<Navigate to="/offer-choice" replace />} />
        <Route path="/dashboard/individual" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/client" element={<Navigate to="/dashboard/individual" replace />} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        
        <Route path="/dashboard/company" element={<ProtectedRoute requireCompany><CompanyDashboard /></ProtectedRoute>} />
        <Route path="/company/profile" element={<ProtectedRoute requireCompany><CompanyProfile /></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute requireCompany><Subscription /></ProtectedRoute>} />
        <Route path="/subscription-success" element={<ProtectedRoute requireCompany><SubscriptionSuccess /></ProtectedRoute>} />
        <Route path="/promotion-success" element={<ProtectedRoute requireCompany><PromotionSuccess /></ProtectedRoute>} />
        
        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        
        <Route path="/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
        <Route path="/regulamin" element={<Regulamin />} />
        <Route path="/polityka-prywatnosci" element={<PolitykaPrywatnosci />} />
        <Route path="/regulamin-platnosci" element={<RegulaminPlatnosci />} />
        
        {/* Materials - Giełda Materiałów */}
        <Route path="/materials" element={<MaterialsList />} />
        <Route path="/materials/new" element={<ProtectedRoute><NewMaterial /></ProtectedRoute>} />
        <Route path="/materials/:materialId" element={<MaterialDetails />} />
        
        {/* Jobs - Oferty Pracy */}
        <Route path="/jobs" element={<JobsList />} />
        <Route path="/jobs/new" element={<ProtectedRoute><NewJob /></ProtectedRoute>} />
        <Route path="/jobs/:jobId" element={<JobDetails />} />
        
        {/* Lokalne strony SEO - miasta */}
        <Route path="/praca-budowlana" element={<CityHub vertical="praca-budowlana" />} />
        <Route path="/praca-budowlana/:citySlug" element={<CityLanding vertical="praca-budowlana" />} />
        <Route path="/remonty" element={<CityHub vertical="remonty" />} />
        <Route path="/remonty/:citySlug" element={<CityLanding vertical="remonty" />} />
        <Route path="/materialy-budowlane" element={<CityHub vertical="materialy-budowlane" />} />
        <Route path="/materialy-budowlane/:citySlug" element={<CityLanding vertical="materialy-budowlane" />} />

        <Route path="/points" element={<Navigate to="/subscription" replace />} />
        <Route path="/payment-success" element={<Navigate to="/subscription-success" replace />} />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <FeatureFlagProvider>
            <ChatProvider>
              <div className="App min-h-screen bg-slate-50">
                <AppRouter />
                <Toaster position="top-right" richColors />
              </div>
            </ChatProvider>
          </FeatureFlagProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
